import { useParams, Link } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { ArrowLeft } from "lucide-react";
import { useSeo } from "../hooks/useSeo";
import { useSettings } from "../context/SettingsContext";
import { NerdBadge } from "../components/NerdBadge";
import articles from "../content/articles.json";

const NERD = { 1: "5th Grader", 2: "Chess Club", 3: "Full Pocket Protector" };
const SITE = "https://practicalaiforeverydaylife.com";

const fmtDate = (iso) =>
  iso ? new Date(iso).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" }) : "";

const mdComponents = {
  h2: (props) => <h2 className="mt-10 font-serif text-3xl text-navy" {...props} />,
  h3: (props) => <h3 className="mt-8 font-serif text-2xl text-navy" {...props} />,
  p: (props) => <p className="mt-5 leading-[1.75]" {...props} />,
  a: (props) => <a className="text-ochre underline underline-offset-2 hover:text-ochre-hover" {...props} />,
  ul: (props) => <ul className="mt-5 list-disc space-y-2 pl-6" {...props} />,
  ol: (props) => <ol className="mt-5 list-decimal space-y-2 pl-6" {...props} />,
  blockquote: (props) => (
    <blockquote
      className="my-6 rounded-r-lg border-l-4 border-ochre bg-white/60 px-5 py-3 font-serif italic text-navy"
      {...props}
    />
  ),
  code: (props) => <code className="rounded bg-navy/8 px-1.5 py-0.5 font-mono text-[0.9em] text-navy" {...props} />,
  img: ({ alt = "", ...props }) => (
    <img className="my-6 rounded-lg" alt={alt} loading="lazy" {...props} />
  ),
};

function readingTime(body) {
  const words = (body || "").split(/\s+/).filter(Boolean).length;
  return Math.max(1, Math.round(words / 200));
}

export default function ArticleDetail() {
  const { slug } = useParams();
  const { settings } = useSettings();
  const article = articles.find((a) => a.type === "post" && a.slug === slug);

  const seoTitle = article ? (article.seoTitle || article.title) : "Article not found";
  const seoDesc = article ? (article.metaDescription || article.excerpt) : "";
  const canonical = article
    ? article.canonical || `${SITE}/articles/${article.slug}`
    : `${SITE}/articles`;

  useSeo({
    title: `${seoTitle} — Practical AI for Everyday Life`,
    description: seoDesc,
    type: "article",
    canonical,
    image: article?.ogImage || undefined,
    jsonLd: article
      ? [
          {
            "@context": "https://schema.org",
            "@type": "BlogPosting",
            headline: article.title,
            description: seoDesc,
            datePublished: article.date,
            dateModified: article.updated || article.date,
            image: (article.ogImage || "/brand/bj-banner.png").startsWith("http")
              ? article.ogImage
              : SITE + (article.ogImage || "/brand/bj-banner.png"),
            author: { "@type": "Person", name: "BJ McCreary", url: `${SITE}/about` },
            mainEntityOfPage: { "@type": "WebPage", "@id": `${SITE}/articles/${article.slug}` },
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: `${SITE}/` },
              { "@type": "ListItem", position: 2, name: "Articles", item: `${SITE}/articles` },
              { "@type": "ListItem", position: 3, name: article.title, item: `${SITE}/articles/${article.slug}` },
            ],
          },
        ]
      : undefined,
  });

  if (!article) {
    return (
      <section className="bg-warm grain" data-testid="article-not-found">
        <div className="container-brand py-28 text-center">
          <h1 className="font-serif text-4xl text-navy">Article not found</h1>
          <p className="mt-3 text-text-muted">This piece doesn't exist (or has moved).</p>
          <Link to="/articles" className="btn btn-primary mt-6">
            ← All articles
          </Link>
        </div>
      </section>
    );
  }

  return (
    <div>
      {/* HEADER */}
      <section className="on-navy bg-navy grain" data-testid="article-header">
        <div className="container-brand py-16 md:py-20">
          <div className="mx-auto max-w-[760px]">
            <p className="eyebrow !text-ochre-bright">Article</p>
            <h1 className="mt-4 font-serif text-4xl leading-[1.1] text-bone md:text-5xl">{article.title}</h1>
            <div className="mt-6 flex flex-wrap items-center gap-x-3 gap-y-2 font-sans text-[13px] text-on-navy-muted">
              <span>{fmtDate(article.date)}</span>
              <span aria-hidden="true">·</span>
              <NerdBadge difficulty={NERD[article.nerdScale]} />
              <span aria-hidden="true">·</span>
              <span>{readingTime(article.body)} min read</span>
            </div>
          </div>
        </div>
      </section>

      {/* BODY */}
      <section className="bg-warm grain" data-testid="article-body">
        <div className="container-brand py-16 md:py-20">
          <article className="mx-auto max-w-[720px] font-serif text-[19px] text-text-body">
            <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>
              {article.body}
            </ReactMarkdown>
          </article>

          <div className="mx-auto mt-14 max-w-[720px] border-t border-navy/10 pt-10 text-center">
            <div className="flex flex-wrap items-center justify-center gap-4">
              <a
                href={settings.youtube_url}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-primary"
                data-testid="article-youtube-cta"
              >
                Subscribe on YouTube
              </a>
              <Link to="/setups" className="btn btn-secondary" data-testid="article-setups-cta">
                Browse the free setups
              </Link>
            </div>
            <div className="mt-8">
              <Link
                to="/articles"
                className="inline-flex items-center gap-2 font-sans text-[13px] font-bold uppercase tracking-[0.12em] text-ochre hover:text-ochre-hover"
                data-testid="article-back-link"
              >
                <ArrowLeft size={15} /> All articles
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
